/**
 * Dismissal Storage Service
 * Manages dismissal state for various reminders with cooldown periods
 * Uses localStorage with user-specific keys
 */

const STORAGE_PREFIX = 'address_reminder_';
const COOLDOWN_DAYS = 30;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

export type ReminderType = 
  | 'announcement' 
  | 'checkout' 
  | 'profile_card';

/**
 * Get storage key for a reminder type and user
 */
function getStorageKey(userId: string, reminderType: ReminderType): string {
  return `${STORAGE_PREFIX}${reminderType}_dismissed_${userId}`;
}

/**
 * Dismiss a reminder for a user
 * Stores current timestamp
 */
export function dismissReminder(userId: string, reminderType: ReminderType): void {
  if (!userId || typeof userId !== 'string') {
    return;
  }

  try {
    const key = getStorageKey(userId, reminderType);
    const timestamp = Date.now();
    localStorage.setItem(key, timestamp.toString());
  } catch (error) {
    // localStorage may be unavailable (private browsing, etc.)
    // Fail silently
  }
}

/**
 * Check if reminder is dismissed within cooldown period
 */
export function isDismissed(
  userId: string, 
  reminderType: ReminderType, 
  cooldownDays: number = COOLDOWN_DAYS
): boolean {
  if (!userId || typeof userId !== 'string') {
    return false;
  }

  try {
    const key = getStorageKey(userId, reminderType);
    const dismissedTimestamp = localStorage.getItem(key);

    if (!dismissedTimestamp) {
      return false; // Never dismissed
    }

    const dismissedTime = parseInt(dismissedTimestamp, 10);
    if (isNaN(dismissedTime)) {
      return false; // Invalid timestamp
    }

    const now = Date.now();
    const cooldownMs = cooldownDays * MILLISECONDS_PER_DAY;
    const timeSinceDismissal = now - dismissedTime;

    // Still within cooldown period
    return timeSinceDismissal < cooldownMs;
  } catch (error) {
    // localStorage may be unavailable
    return false; // Show reminder if we can't check
  }
}

/**
 * Check if reminder should be shown
 * Returns true if reminder should be shown (not dismissed or cooldown expired)
 */
export function shouldShowReminder(
  userId: string,
  reminderType: ReminderType,
  cooldownDays: number = COOLDOWN_DAYS
): boolean {
  return !isDismissed(userId, reminderType, cooldownDays);
}

/**
 * Clear dismissal for a reminder (for testing or reset)
 */
export function clearDismissal(userId: string, reminderType: ReminderType): void {
  if (!userId || typeof userId !== 'string') {
    return;
  }

  try {
    const key = getStorageKey(userId, reminderType);
    localStorage.removeItem(key);
  } catch (error) {
    // Fail silently
  }
}

/**
 * Get days remaining in cooldown
 */
export function getCooldownDaysRemaining(
  userId: string,
  reminderType: ReminderType,
  cooldownDays: number = COOLDOWN_DAYS
): number {
  if (!userId || typeof userId !== 'string') {
    return 0;
  }

  try {
    const key = getStorageKey(userId, reminderType);
    const dismissedTimestamp = localStorage.getItem(key);

    if (!dismissedTimestamp) {
      return 0; // Never dismissed
    }

    const dismissedTime = parseInt(dismissedTimestamp, 10);
    if (isNaN(dismissedTime)) {
      return 0;
    }

    const now = Date.now();
    const cooldownMs = cooldownDays * MILLISECONDS_PER_DAY;
    const timeSinceDismissal = now - dismissedTime;

    if (timeSinceDismissal >= cooldownMs) {
      return 0; // Cooldown expired
    }

    const remainingMs = cooldownMs - timeSinceDismissal;
    return Math.ceil(remainingMs / MILLISECONDS_PER_DAY);
  } catch (error) {
    return 0;
  }
}

